import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../auth/state';
import feedbackManager from './feedback_manager';
import './feedback_user.css';
import DashboardFooter from '../../components/DashboardFooter';
import DashboardHeader from '../../components/DashboardHeader';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../auth/firebase';
import cacheManager from '../../auth/cache_manager';

function UserFeedback() {
  const navigate = useNavigate();
  const { user, userProfile } = useAuthStore();
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }

        // # Dispatch theme change event for footer and other components
        const themeChangeEvent = new CustomEvent('theme_change', {
          detail: { isDark: newTheme }
        });
        window.dispatchEvent(themeChangeEvent);
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Handle feedback submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!feedback.trim()) return;

    setIsSubmitting(true);
    try {
      await feedbackManager.addFeedback({
        email: user.email,
        name: userProfile?.firstName 
          ? `${userProfile.firstName} ${userProfile.lastName}`
          : user.email.split('@')[0],
        content: feedback
      });

      setSubmitStatus({
        type: 'success',
        message: 'Thank you for your feedback!'
      });
      setFeedback('');
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: 'Error submitting feedback. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`feedback-container ${isDarkMode ? 'feedback-dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />

      <main className="feedback-main">
        <div className="feedback-content">
          <h2>Share Your Thoughts</h2>
          <p>We value your feedback to improve our services.</p>

          <form onSubmit={handleSubmit} className="feedback-form">
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Write your feedback here..."
              maxLength={1000}
              required
            />
            <div className="char-count">
              {feedback.length}/1000 characters
            </div>

            {submitStatus && (
              <div className={`status-message ${submitStatus.type}`}>
                {submitStatus.message}
              </div>
            )}

            <button 
              type="submit" 
              disabled={isSubmitting || !feedback.trim()}
              className="submit-button"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
            </button>
          </form>
        </div>
      </main>

      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
}

export default UserFeedback;
